import React, { useEffect, useRef, useState } from 'react'
import { postVersionChatMessage } from 'api/essays'
import Button from 'shared/ui/Button/Button'
import { ReactComponent as CloseIcon } from 'assets/icons/cross.svg'
import s from './FeedbackPanel.module.scss'

const FeedbackPanel = ({
	essayId,
	versionId,
	open,
	expanded = false,
	items = [],
	loading,
	onReload,
	onClose,
	showHead = true,
	activeFeedbackId,
	highlightedFeedbackId = null,
	selection = null,
	onClearSelection,
	onSend,
	sendDisabled = false,
}) => {
	const [value, setValue] = useState('')
	const [sending, setSending] = useState(false)
	// Set synchronously on the first click: state alone lets two clicks in one frame both send.
	const sendingRef = useRef(false)
	const itemRefs = useRef({})
	const listRef = useRef(null)
	const inputRef = useRef(null)
	const prevLoadingRef = useRef(loading)
	const prevItemsLenRef = useRef(items.length)

	useEffect(() => {
		if (!highlightedFeedbackId) return
		const el = itemRefs.current[highlightedFeedbackId]
		if (!el) return
		el.scrollIntoView({ behavior: 'smooth', block: 'center' })
	}, [highlightedFeedbackId, items])

	useEffect(() => {
		const wasLoading = prevLoadingRef.current
		prevLoadingRef.current = loading
		const prevLen = prevItemsLenRef.current
		prevItemsLenRef.current = items.length
		const el = listRef.current
		if (!el) return
		if (wasLoading && !loading && items.length) {
			el.scrollTop = el.scrollHeight
			return
		}
		if (items.length > prevLen) {
			el.scrollTop = el.scrollHeight
		}
	}, [loading, items])

	if (!open) return null

	const panelCls = [s.panel, expanded ? s.wide : ''].filter(Boolean).join(' ')
	const canSend =
		Boolean(value.trim()) &&
		Boolean(essayId) &&
		Boolean(versionId) &&
		!loading &&
		!sendDisabled &&
		!sending

	const handleSend = async () => {
		const text = value.trim()
		if (!text || !essayId || !versionId || sendingRef.current || sendDisabled) return
		sendingRef.current = true
		setSending(true)
		try {
			if (typeof onSend === 'function') {
				await onSend(text)
			} else {
				await postVersionChatMessage(essayId, versionId, {
					message_text: text,
				})
				onReload?.()
			}
			setValue('')
		} finally {
			sendingRef.current = false
			setSending(false)
		}
	}

	return (
		<aside className={panelCls}>
			{showHead ? (
				<div className={s.head}>
					<h3 className={s.title}>Feedback</h3>
					{onClose ? (
						<button
							type="button"
							className={s.closeBtn}
							onClick={onClose}
							aria-label="Close feedback panel"
						>
							<CloseIcon />
						</button>
					) : null}
				</div>
			) : null}
			<div className={s.list} ref={listRef}>
				{loading && !items.length ? (
					<p className={s.empty}>Loading feedback...</p>
				) : null}
				{items.map(message => {
					const isMine = message.senderType !== 'student'
					const cls = [
						s.message,
						isMine ? s.mine : s.theirs,
						activeFeedbackId === message.id ? s.active : '',
						highlightedFeedbackId === message.id ? s.highlighted : '',
					]
						.filter(Boolean)
						.join(' ')
					const displayName = isMine ? 'You' : message.author || 'Student'
					return (
						<div
							key={message.id}
							className={cls}
							ref={el => {
								if (el) itemRefs.current[message.id] = el
								else delete itemRefs.current[message.id]
							}}
						>
							<div className={s.metaRow}>
								{isMine ? (
									<>
										{message.timeLabel ? (
											<span className={s.time}>{message.timeLabel}</span>
										) : null}
										<span className={s.author}>{displayName}</span>
										{message.isUnread ? (
											<span className={s.newPill}>NEW</span>
										) : null}
									</>
								) : (
									<>
										<span className={s.author}>{displayName}</span>
										{message.timeLabel ? (
											<span className={s.time}>{message.timeLabel}</span>
										) : null}
										{message.isUnread ? (
											<span className={s.newPill}>NEW</span>
										) : null}
									</>
								)}
							</div>
							<div className={s.bubble}>
								{message.anchor?.text ? (
									<div className={s.anchor}>{message.anchor.text}</div>
								) : null}
								<p className={s.text}>{message.text}</p>
							</div>
						</div>
					)
				})}
				{!loading && !items.length ? (
					<p className={s.empty}>No feedback messages yet.</p>
				) : null}
			</div>
			<div className={s.composer}>
				{selection?.text ? (
					<div className={s.selectionPreview}>
						<span className={s.selectionPreview__text} title={selection.text}>
							{selection.text}
						</span>
						<button
							type="button"
							className={s.selectionPreview__clear}
							onClick={onClearSelection}
						>
							×
						</button>
					</div>
				) : null}
				<div
					className={s.composerBox}
					onClick={() => {
						if (sending || sendDisabled) return
						inputRef.current?.focus()
					}}
				>
					<textarea
						ref={inputRef}
						className={s.input}
						value={value}
						onChange={e => setValue(e.target.value)}
						placeholder="Write feedback..."
						disabled={sending || sendDisabled}
					/>
					<div className={s.composerActions}>
						<Button size="sm" onClick={handleSend} disabled={!canSend}>
							{sending ? (
								<span className={s.sendSpinner} aria-label="Sending" />
							) : (
								'Send'
							)}
						</Button>
					</div>
				</div>
			</div>
		</aside>
	)
}

export default FeedbackPanel
